import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Navigation } from "lucide-react";
import { SITE, waLink } from "@/lib/site";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-brand-ink text-brand-bg/90">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
        <div className="grid gap-12 md:grid-cols-12">
          {/* Brand + About */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span
                aria-label="ENT logo"
                className="inline-flex items-center rounded-lg bg-white px-2 py-1"
              >
                <img src="/ent-logo.png" alt="ENT logo — ear, nose, throat" className="h-9 w-auto" />
              </span>
              <span className="font-heading font-bold text-2xl">{SITE.doctorName}</span>
            </div>
            <p
              data-testid="footer-about"
              className="text-sm leading-relaxed text-brand-bg/65 max-w-md"
            >
              Dr. Sanjay Teza is a trusted ENT Specialist &amp; ENT Surgeon in Varanasi with over 25
              years of experience. He provides evidence-based diagnosis and advanced treatment for
              ear, nose, throat, sinus, hearing and voice disorders. Committed to ethical,
              compassionate care for patients of all ages.
            </p>
          </div>

          {/* Explore */}
          <div className="md:col-span-2">
            <h4 className="text-xs tracking-[0.2em] uppercase text-brand-teal mb-5">Explore</h4>
            <ul className="space-y-3">
              {SITE.nav.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={`footer-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-brand-bg/70 hover:text-brand-teal transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-5">
            <h4 className="text-xs tracking-[0.2em] uppercase text-brand-teal mb-5">Contact</h4>
            <ul className="space-y-4 text-sm text-brand-bg/75">
              <li className="flex gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-brand-teal shrink-0" />
                <a
                  href={`tel:${SITE.phoneRaw}`}
                  data-testid="footer-phone-link"
                  className="hover:text-brand-teal transition-colors"
                >
                  {SITE.phoneDisplay}
                </a>
              </li>
              <li className="flex gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-brand-teal shrink-0" />
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="footer-email-link"
                  className="hover:text-brand-teal transition-colors"
                >
                  {SITE.email}
                </a>
              </li>
            </ul>
            <a
              href={waLink(`Hello, I would like to book an appointment with ${SITE.doctorName}.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-whatsapp px-6 py-3 text-sm text-white transition-colors hover:bg-whatsapp-hover"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>

        {/* Locations — both clinics */}
        <div className="mt-16 pt-12 border-t border-brand-bg/15">
          <h4 className="text-xs tracking-[0.2em] uppercase text-brand-teal mb-8">Clinic Locations</h4>
          <div className="grid gap-8 md:grid-cols-2" data-testid="footer-locations">
            {SITE.clinics.map((c, idx) => (
              <div
                key={c.name}
                data-testid={`footer-clinic-${idx}`}
                className="rounded-2xl border border-brand-bg/12 p-6"
              >
                <div className="flex items-start justify-between gap-3">
                  <h5 className="font-heading text-lg text-brand-bg">{c.name}</h5>
                  <span className="text-[10px] tracking-[0.16em] uppercase text-brand-teal bg-brand-teal/10 rounded-full px-3 py-1 shrink-0">
                    {c.area}
                  </span>
                </div>
                <ul className="mt-4 space-y-3 text-sm text-brand-bg/70">
                  <li className="flex gap-3">
                    <MapPin className="w-4 h-4 mt-0.5 text-brand-teal shrink-0" />
                    <span className="leading-relaxed">{c.addressFull}</span>
                  </li>
                  <li className="flex gap-3">
                    <Clock className="w-4 h-4 mt-0.5 text-brand-teal shrink-0" />
                    <span>{c.timings}</span>
                  </li>
                </ul>
                <a
                  href={c.gbpLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-clinic-directions-${idx}`}
                  className="mt-5 inline-flex items-center gap-2 text-sm text-brand-teal hover:gap-3 transition-[gap] duration-200"
                >
                  <Navigation className="w-4 h-4" /> Get directions
                </a>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-brand-bg/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-brand-bg/50">
          <p>© {new Date().getFullYear()} {SITE.clinicName}. All rights reserved.</p>
          <p className="max-w-xl">
            The information on this website is for general awareness and does not replace a
            professional medical consultation.
          </p>
        </div>
      </div>
    </footer>
  );
}
